import React, { useState, useEffect, useMemo } from "react";
import { Helmet } from 'react-helmet-async';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { useTheme } from "../context/ThemeContext";
import { useNavigate } from "react-router-dom";
import {
  Code,
  Copy,
  ExternalLink,
  Settings,
  Eye,
  Lightbulb,
  AlertCircle,
  MoreHorizontal,
  BookOpen,
  Bell,
  ExternalLink as ExternalLinkIcon,
  X,
  Moon,
  Sun,
  ArrowLeft,
  FolderOpen // Add FolderOpen icon
} from "lucide-react";
import { toast } from "sonner";

export const Widget = () => {
  const { user, activeProject, projects, switchProject } = useAuth();
  const { language, t } = useLanguage();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState("customize");
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [selectedFeedbackType, setSelectedFeedbackType] = useState("feature");
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [selectedFramework, setSelectedFramework] = useState("react");
  const [feedbackForm, setFeedbackForm] = useState({
    title: "",
    description: "",
    type: "feature",
    submitter_name: "",
    submitter_email: "",
  });

  // Default widget ayarları (Responsive Widget)
  const defaultWidgetSettings = {
    primaryColor: "#0891b2",
    backgroundColor: "#ffffff",
    buttonStyle: "rounded-full",
    fontSize: "16px",
    fontFamily: "Inter, sans-serif",
    buttonSize: "lg",
    theme: "light", // light veya dark
    containerSelector: "", // P2 FİKS: Custom container seçeneği
  };

  // Widget özelleştirme ayarları (kullanıcının düzenlediği)
  const [widgetSettings, setWidgetSettings] = useState({
    ...defaultWidgetSettings,
  });

  // Kaydedilmiş ayarlar (kod üretiminde kullanılacak)
  const [savedWidgetSettings, setSavedWidgetSettings] = useState({
    ...defaultWidgetSettings,
  });

  // Proje değiştiğinde widget ayarlarını sıfırla
  useEffect(() => {
    if (activeProject?.id) {
      setWidgetSettings({ ...defaultWidgetSettings });
      setSavedWidgetSettings({ ...defaultWidgetSettings });
    }
  }, [activeProject?.id]);

  // Uyarı modal state
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] =
    useState(false);
  const [pendingTabChange, setPendingTabChange] = useState(null);

  // Buton her zaman orta altta sabit
  const buttonPosition = {
    bottom: "24px",
    left: "50%",
    transform: "translateX(-50%)",
  };

  // Framework'lere göre widget kodunu oluştur
  // NOT: Kaydedilmiş ayarları kullan (savedWidgetSettings), böylece kaydedilmemiş değişiklikler koda yansımaz
  const generateWidgetCode = (framework, lang = language, translations = t) => {
    const baseConfig = {
      tenantId: activeProject?.id || user?.tenant_id,
      primaryColor: savedWidgetSettings.primaryColor,
      backgroundColor: savedWidgetSettings.backgroundColor,
      buttonStyle: savedWidgetSettings.buttonStyle,
      fontSize: savedWidgetSettings.fontSize,
      fontFamily: savedWidgetSettings.fontFamily,
      theme: savedWidgetSettings.theme,
      language: lang, // FeedPanels'te seçilen dili entegrasyon'a ilet
      // P2 FİKS: Container seçeneğini dahil et (eğer varsa)
      ...(savedWidgetSettings.containerSelector && {
        container: savedWidgetSettings.containerSelector,
      }),
    };

    const configString = JSON.stringify(baseConfig, null, 2);

    // Installation Instructions (5 Dil Desteği)
    const installationInstructions = {
      tr: `// ⚙️ Kurulum Talimatları:
// 1. Yukarıdaki bileşen kodunu kopyalayın
// 2. Projenizin uygun bir konumuna ekleyin (örneğin, App.js veya Layout bileşeni)
// 3. Bileşeni içe aktarın ve render edin: <FeedbackWidget tenantId="${baseConfig.tenantId}" />
// 4. Değişiklikleri kaydedin ve uygulamanızı yenileyin
// 5. Bileşen render edildiğinde widget düğmesinin göründüğünü kontrol edin
//
// 🌍 Dil Desteği:
// - FeedPanels'teki dil: Varsayılan olarak kullanılır (opsiyonel)
// - Özel dil: <FeedbackWidget tenantId="${baseConfig.tenantId}" language="en" />
// - Desteklenen diller: tr (Türkçe), en (İngilizce), de (Almanca), es (İspanyolca), fr (Fransızca)
// - Dinamik dil değişimi: language prop'u değişirse widget dili anında güncellenir
// - Tenant ID'niz: ${baseConfig.tenantId}
// Örnek: const { language } = useLanguage(); <FeedbackWidget tenantId="${baseConfig.tenantId}" language={language} />`,

      en: `// ⚙️ Installation Instructions:
// 1. Copy the component code above
// 2. Add the component to an appropriate location in your project (e.g., App.js or Layout)
// 3. Import and render the component: <FeedbackWidget tenantId="${baseConfig.tenantId}" />
// 4. Save changes and refresh your application
// 5. Check that the widget button appears when the component is rendered
//
// 🌍 Language Support:
// - FeedPanels language: Used by default (optional)
// - Custom language: <FeedbackWidget tenantId="${baseConfig.tenantId}" language="en" />
// - Supported languages: tr (Turkish), en (English), de (German), es (Spanish), fr (French)
// - Dynamic language change: Widget language updates instantly when language prop changes
// - Your Tenant ID: ${baseConfig.tenantId}
// Example: const { language } = useLanguage(); <FeedbackWidget tenantId="${baseConfig.tenantId}" language={language} />`,

      de: `// ⚙️ Installationsanleitung:
// 1. Kopieren Sie den Komponentencode oben
// 2. Fügen Sie die Komponente an einem geeigneten Ort in Ihrem Projekt ein (z. B. App.js oder Layout)
// 3. Importieren und rendern Sie die Komponente: <FeedbackWidget tenantId="${baseConfig.tenantId}" />
// 4. Speichern Sie Änderungen und aktualisieren Sie Ihre Anwendung
// 5. Überprüfen Sie, ob die Widget-Schaltfläche angezeigt wird, wenn die Komponente gerendert wird
//
// 🌍 Sprachunterstützung:
// - FeedPanels-Sprache: Wird standardmäßig verwendet (optional)
// - Benutzerdefinierte Sprache: <FeedbackWidget tenantId="${baseConfig.tenantId}" language="de" />
// - Unterstützte Sprachen: tr (Türkisch), en (Englisch), de (Deutsch), es (Spanisch), fr (Französisch)
// - Dynamischer Sprachwechsel: Widget-Sprache wird sofort aktualisiert, wenn sich language ändert
// - Ihre Mandanten-ID: ${baseConfig.tenantId}
// Beispiel: const { language } = useLanguage(); <FeedbackWidget tenantId="${baseConfig.tenantId}" language={language} />`,

      es: `// ⚙️ Instrucciones de Instalación:
// 1. Copie el código del componente anterior
// 2. Agregue el componente a una ubicación apropiada en su proyecto (p. ej., App.js o Layout)
// 3. Importe y renderice el componente: <FeedbackWidget tenantId="${baseConfig.tenantId}" />
// 4. Guarde los cambios y actualice su aplicación
// 5. Verifique que el botón del widget aparezca cuando se renderice el componente
//
// 🌍 Compatibilidad de Idiomas:
// - Idioma de FeedPanels: Se utiliza por defecto (opcional)
// - Idioma personalizado: <FeedbackWidget tenantId="${baseConfig.tenantId}" language="es" />
// - Idiomas compatibles: tr (Turco), en (Inglés), de (Alemán), es (Español), fr (Francés)
// - Cambio dinámico de idioma: El idioma del widget se actualiza instantáneamente cuando cambia language
// - Su ID de Inquilino: ${baseConfig.tenantId}
// Ejemplo: const { language } = useLanguage(); <FeedbackWidget tenantId="${baseConfig.tenantId}" language={language} />`,

      fr: `// ⚙️ Instructions d'Installation:
// 1. Copiez le code du composant ci-dessus
// 2. Ajoutez le composant à un endroit approprié de votre projet (par exemple, App.js ou Layout)
// 3. Importez et rendez le composant: <FeedbackWidget tenantId="${baseConfig.tenantId}" />
// 4. Enregistrez les modifications et actualisez votre application
// 5. Vérifiez que le bouton du widget apparaît lorsque le composant est rendu
//
// 🌍 Support Multilingue:
// - Langue FeedPanels: Utilisée par défaut (optionnel)
// - Langue personnalisée: <FeedbackWidget tenantId="${baseConfig.tenantId}" language="fr" />
// - Langues supportées: tr (Turc), en (Anglais), de (Allemand), es (Espagnol), fr (Français)
// - Changement dynamique de langue: La langue du widget se met à jour instantanément quand language change
// - Votre ID de Locataire: ${baseConfig.tenantId}
// Exemple: const { language } = useLanguage(); <FeedbackWidget tenantId="${baseConfig.tenantId}" language={language} />`,
    };

    const instructions = installationInstructions[lang] || installationInstructions['en'];

    switch (framework) {
      case "react":
        const reactComments = {
          tr: "// Seçili dili widget'a ilet",
          en: "// Pass selected language to widget",
          de: "// Ausgewählte Sprache an das Widget übergeben",
          es: "// Pasar idioma seleccionado al widget",
          fr: "// Passer la langue sélectionnée au widget"
        };
        return `import { useEffect } from 'react';

export default function FeedbackWidget({
  tenantId,
  widgetUrl = "https://feedpanels.com/widget.js",
  primaryColor = "${savedWidgetSettings.primaryColor}",
  backgroundColor = "${savedWidgetSettings.backgroundColor}",
  buttonStyle = "${savedWidgetSettings.buttonStyle}",
  fontSize = "${savedWidgetSettings.fontSize}",
  fontFamily = "${savedWidgetSettings.fontFamily}",
  theme = "${savedWidgetSettings.theme}",
  language = "${lang}" ${lang === 'tr' ? '// Dil seçimi (tr, en, de, es, fr)' : '// Language selection (tr, en, de, es, fr)'}
}) {
  useEffect(() => {
    if (!tenantId) {
      console.error('FeedbackWidget: tenantId is required');
      return;
    }

    const script = document.createElement('script');
    script.src = widgetUrl;
    script.async = true;
    document.body.appendChild(script);

    script.onload = () => {
      if (window.fbw) {
        window.fbw('init', {
          tenantId: tenantId,
          primaryColor: primaryColor,
          backgroundColor: backgroundColor,
          buttonStyle: buttonStyle,
          fontSize: fontSize,
          fontFamily: fontFamily,
          theme: theme,
          language: language ${reactComments[lang] || reactComments['en']}
        });
      }
    };

    script.onerror = () => {
      console.error('Failed to load FeedbackFlow widget script');
      console.error(\`Widget URL: \${widgetUrl}\`);
    };

    return () => {
      const scripts = document.querySelectorAll('script[src*="widget.js"]');
      scripts.forEach(s => s.remove());
    };
  }, [tenantId, widgetUrl, primaryColor, backgroundColor, buttonStyle, fontSize, fontFamily, theme, language]);

  return null;
}

${instructions}`;

      case "vue":
        const vueComments = {
          tr: "// Dil seçimi (tr, en, de, es, fr)",
          en: "// Language selection (tr, en, de, es, fr)",
          de: "// Sprachauswahl (tr, en, de, es, fr)",
          es: "// Selección de idioma (tr, en, de, es, fr)",
          fr: "// Sélection de la langue (tr, en, de, es, fr)"
        };
        const vuePassComment = {
          tr: "// Seçili dili widget'a ilet",
          en: "// Pass selected language to widget",
          de: "// Ausgewählte Sprache an das Widget übergeben",
          es: "// Pasar idioma seleccionado al widget",
          fr: "// Passer la langue sélectionnée au widget"
        };
        return `<template>
  <div></div>
</template>

<script>
export default {
  name: 'FeedbackWidget',
  props: {
    tenantId: {
      type: String,
      required: true
    },
    widgetUrl: {
      type: String,
      default: 'https://feedpanels.com/widget.js'
    },
    primaryColor: {
      type: String,
      default: '${savedWidgetSettings.primaryColor}'
    },
    backgroundColor: {
      type: String,
      default: '${savedWidgetSettings.backgroundColor}'
    },
    buttonStyle: {
      type: String,
      default: '${savedWidgetSettings.buttonStyle}'
    },
    fontSize: {
      type: String,
      default: '${savedWidgetSettings.fontSize}'
    },
    fontFamily: {
      type: String,
      default: '${savedWidgetSettings.fontFamily}'
    },
    theme: {
      type: String,
      default: '${savedWidgetSettings.theme}'
    },
    language: {
      type: String,
      default: '${lang}' ${vueComments[lang] || vueComments['en']}
    }
  },
  mounted() {
    if (!this.tenantId) {
      console.error('FeedbackWidget: tenantId is required');
      return;
    }

    const script = document.createElement('script');
    script.src = this.widgetUrl;
    script.async = true;
    document.body.appendChild(script);

    script.onload = () => {
      if (window.fbw) {
        window.fbw('init', {
          tenantId: this.tenantId,
          primaryColor: this.primaryColor,
          backgroundColor: this.backgroundColor,
          buttonStyle: this.buttonStyle,
          fontSize: this.fontSize,
          fontFamily: this.fontFamily,
          theme: this.theme,
          language: this.language ${vuePassComment[lang] || vuePassComment['en']}
        });
      }
    };

    script.onerror = () => {
      console.error('Failed to load FeedbackFlow widget script');
      console.error(\`Widget URL: \${this.widgetUrl}\`);
    };
  },
  beforeUnmount() {
    const scripts = document.querySelectorAll('script[src*="widget.js"]');
    scripts.forEach(script => script.remove());
  }
};
</script>

${instructions}`;

      case "nuxt":
        return `// ${t.widgetNuxtPluginComment1}
// ${t.widgetNuxtPluginComment2}
// ${t.widgetNuxtPluginComment3.replace("{tenantId}", baseConfig.tenantId)}

export default defineNuxtPlugin(() => {
  if (process.client) {
    const script = document.createElement('script');
    // ${t.widgetWpUrlComment}
    const widgetUrl = 'https://feedpanels.com/widget.js';
    script.src = widgetUrl;
    script.async = true;
    document.body.appendChild(script);

    script.onload = () => {
      if (window.fbw) {
        window.fbw('init', ${configString});
      }
    };

    script.onerror = () => {
      console.error('Failed to load FeedbackFlow widget script');
      console.error(\`Widget URL: \${widgetUrl}\`);
    };
  }
});

${instructions}`;

      case "next":
        const nextComments = {
          tr: "// Seçili dili widget'a ilet",
          en: "// Pass selected language to widget",
          de: "// Ausgewählte Sprache an das Widget übergeben",
          es: "// Pasar idioma seleccionado al widget",
          fr: "// Passer la langue sélectionnée au widget"
        };
        return `// components/FeedbackWidget.tsx
'use client';

import { useEffect } from 'react';

interface FeedbackWidgetProps {
  tenantId: string;
  widgetUrl?: string;
  primaryColor?: string;
  backgroundColor?: string;
  buttonStyle?: string;
  fontSize?: string;
  fontFamily?: string;
  theme?: string;
  language?: string;
}

export default function FeedbackWidget({
  tenantId,
  widgetUrl = "https://feedpanels.com/widget.js",
  primaryColor = "${savedWidgetSettings.primaryColor}",
  backgroundColor = "${savedWidgetSettings.backgroundColor}",
  buttonStyle = "${savedWidgetSettings.buttonStyle}",
  fontSize = "${savedWidgetSettings.fontSize}",
  fontFamily = "${savedWidgetSettings.fontFamily}",
  theme = "${savedWidgetSettings.theme}",
  language = "${lang}"
}: FeedbackWidgetProps) {
  useEffect(() => {
    if (!tenantId) {
      console.error('FeedbackWidget: tenantId is required');
      return;
    }

    const script = document.createElement('script');
    script.src = widgetUrl;
    script.async = true;
    document.body.appendChild(script);

    script.onload = () => {
      if (window.fbw) {
        window.fbw('init', {
          tenantId: tenantId,
          primaryColor: primaryColor,
          backgroundColor: backgroundColor,
          buttonStyle: buttonStyle,
          fontSize: fontSize,
          fontFamily: fontFamily,
          theme: theme,
          language: language ${nextComments[lang] || nextComments['en']}
        });
      }
    };

    script.onerror = () => {
      console.error('Failed to load FeedbackFlow widget script');
      console.error(\`Widget URL: \${widgetUrl}\`);
    };

    return () => {
      const scripts = document.querySelectorAll('script[src*="widget.js"]');
      scripts.forEach(s => s.remove());
    };
  }, [tenantId, widgetUrl, primaryColor, backgroundColor, buttonStyle, fontSize, fontFamily, theme, language]);

  return null;
}

${instructions}`;

      case "wordpress":
        return `<!-- ${t.wordpressCodeComment} -->
<!-- Widget tenantId: ${baseConfig.tenantId} -->
<?php
function add_feedback_widget() {
    // ${t.widgetWpUrlComment}
    \$widgetUrl = 'https://feedpanels.com/widget.js';
    ?>
    <script>
    (function(w,d,s,o,f,js,fjs){
      w['FeedbackWidget']=o;
      w[o]=w[o]||function(){(w[o].q=w[o].q||[]).push(arguments)};
      js=d.createElement(s);
      js.src=f;
      js.async=1;
      fjs=d.getElementsByTagName(s)[0];
      fjs.parentNode.insertBefore(js,fjs);
    })(window,document,'script','fbw','<?php echo \$widgetUrl; ?>');

    // Widget configuration with tenantId
    fbw('init', ${configString});
    </script>
    <?php
}
add_action('wp_footer', 'add_feedback_widget');
?>

${instructions}`;

      case "other":
        return `${translations.otherUsage}

${translations.otherInstallation}

${translations.otherInstallationDescription}

<!-- ${t.widgetOtherScriptComment} -->
<!-- Widget configuration - tenantId: ${baseConfig.tenantId} -->
<script src="https://feedpanels.com/widget.js"></script>
<script>
  window.addEventListener('load', function() {
    if (window.fbw) {
      // Initialize widget with tenantId
      window.fbw('init', ${configString});
    }
  });
</script>

${translations.otherFeature1}

${translations.otherFeature1Description}

${translations.otherFeature1Details}
${translations.otherFeature1Item1}
${translations.otherFeature1Item2}
${translations.otherFeature1Item3}
${translations.otherFeature1Item4 ? translations.otherFeature1Item4 : ""}

${translations.otherFeature2}

${translations.otherFeature2Description}

${translations.otherFeature3}

${translations.otherFeature3Description}

${translations.otherCustomization}

${translations.otherCustomizationDescription}

${translations.otherParam1}
${translations.otherParam2}
${translations.otherParam3}
${translations.otherParam4}
${translations.otherParam5}
${translations.otherParam6}
${translations.otherParam7}

${translations.otherExample}

${translations.otherExampleBasic}

${t.widgetOtherScriptComment} -->
<script src="https://feedpanels.com/widget.js"></script>
<script>
  window.addEventListener('load', function() {
    if (window.fbw) {
      window.fbw('init', ${configString});
    }
  });
</script>

${translations.otherNote}

${instructions}`;

      default:
        return generateWidgetCode("react");
    }
  };

  // Widget kodunu seçilen framework'e göre oluştur
  // NOT: savedWidgetSettings kullan, böylece kaydedilmemiş değişiklikler koda yansımaz
  const widgetCode = useMemo(() => {
    return generateWidgetCode(selectedFramework, language, t);
  }, [
    user?.tenant_id,
    activeProject?.id,
    savedWidgetSettings,
    selectedFramework,
    language,
    t,
  ]);

  const handleCopy = async () => {
    try {
      // Versuche die moderne Clipboard API zu verwenden
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(widgetCode);
      } else {
        // Fallback für ältere Browser oder unsichere Kontexte
        const textArea = document.createElement("textarea");
        textArea.value = widgetCode;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
          const successful = document.execCommand("copy");
          if (!successful) {
            throw new Error("execCommand failed");
          }
        } finally {
          document.body.removeChild(textArea);
        }
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Fehler beim Kopieren:", err);
      const errorMessages = {
        tr: "Kopyalama başarısız oldu. Lütfen manuel olarak kopyalayın.",
        en: "Copy failed. Please copy manually.",
        de: "Kopieren fehlgeschlagen. Bitte manuell kopieren.",
        es: "Error al copiar. Por favor copie manualmente.",
        fr: "Échec de la copie. Veuillez copier manuellement.",
      };
      toast.error(errorMessages[language] || errorMessages.tr);
    }
  };

  const handleSettingChange = (key, value) => {
    setWidgetSettings((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Ayarların değişip değişmediğini kontrol et
  // widgetSettings (kullanıcının düzenlediği) ile savedWidgetSettings (kaydedilmiş) karşılaştır
  const hasUnsavedChanges = () => {
    return (
      JSON.stringify(widgetSettings) !== JSON.stringify(savedWidgetSettings)
    );
  };

  // Tab değişikliğini handle et
  const handleTabChange = (newTab) => {
    // Eğer customize tab'ından code tab'ına geçiliyorsa ve değişiklik varsa
    if (activeTab === "customize" && newTab === "code" && hasUnsavedChanges()) {
      setPendingTabChange(newTab);
      setShowUnsavedChangesDialog(true);
    } else {
      // Değişiklik yoksa veya başka bir tab'a geçiliyorsa direkt geç
      setActiveTab(newTab);
    }
  };

  // Uyarıyı onayla - değişiklikleri kaydetmeden geç
  const handleConfirmTabChange = () => {
    setShowUnsavedChangesDialog(false);
    if (pendingTabChange) {
      setActiveTab(pendingTabChange);
      setPendingTabChange(null);
    }
  };

  // Uyarıyı iptal et - customize tab'ında kal
  const handleCancelTabChange = () => {
    setShowUnsavedChangesDialog(false);
    setPendingTabChange(null);
  };

  return (
    <>
      <Helmet>
        <title>Widget Integration - FeedPanels</title>
        <meta name="description" content="Get FeedPanels widget install code for your site. Copy-paste snippets for React, Vue, Next.js, Nuxt, WordPress, or plain HTML." />
        <link rel="canonical" href="https://feedpanels.com/dashboard/widget" />
      </Helmet>
      <div className="p-4 md:p-6 lg:p-8">
        <div className="max-w-6xl mx-auto">
          <div className="mb-6 md:mb-8">
            <h1
              className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2"
              data-testid="widget-page-title"
            >
              {t.widgetIntegration}
            </h1>
            <p className="text-sm md:text-base text-gray-600">{t.widgetDescription}</p>
          </div>

          <Tabs
            value={activeTab}
            onValueChange={handleTabChange}
            className="space-y-4 md:space-y-6"
          >
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger
                value="customize"
                className="flex items-center gap-1 md:gap-2 text-xs md:text-sm"
              >
                <Settings className="w-3 md:w-4 h-3 md:h-4" />
                {t.customize}
              </TabsTrigger>
              <TabsTrigger value="code" className="flex items-center gap-1 md:gap-2 text-xs md:text-sm">
                <Code className="w-3 md:w-4 h-3 md:h-4" />
                {t.integrationCode}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="customize" className="space-y-4 md:space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-6">
                {/* Özelleştirme Formu */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg md:text-xl">{t.widgetSettings}</CardTitle>
                    <CardDescription className="text-xs md:text-sm">
                      {t.widgetSettingsDescription}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4 md:space-y-6">
                    {/* Project Selector (Integration Tab) */}
                    {projects.length > 0 && activeProject && (
                      <div className="space-y-2">
                        <Label className="text-xs md:text-sm text-gray-500 dark:text-gray-400 text-[11px] md:text-xs px-1">{t.activeProject}</Label>
                        <Select
                          value={activeProject.id}
                          onValueChange={switchProject}
                        >
                          <SelectTrigger className="w-full text-sm md:text-base" data-testid="project-selector-widget">
                            <SelectValue>
                              <div className="flex items-center space-x-2 truncate">
                                <FolderOpen className="w-3 md:w-4 h-3 md:h-4 flex-shrink-0" />
                                <span className="truncate">{activeProject.name}</span>
                              </div>
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {projects.map((project) => (
                              <SelectItem key={project.id} value={project.id}>
                                {project.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {/* Ana Renk */}
                    <div className="space-y-2">
                      <Label htmlFor="primaryColor" className="text-xs md:text-sm">{t.buttonColor}</Label>
                      <div className="flex gap-2">
                        <Input
                          id="primaryColor"
                          type="color"
                          value={widgetSettings.primaryColor}
                          onChange={(e) =>
                            handleSettingChange("primaryColor", e.target.value)
                          }
                          className="w-16 md:w-20 h-9 md:h-10"
                        />
                        <Input
                          value={widgetSettings.primaryColor}
                          onChange={(e) =>
                            handleSettingChange("primaryColor", e.target.value)
                          }
                          placeholder="#0891b2"
                          className="flex-1 text-xs md:text-sm"
                        />
                      </div>
                    </div>

                    {/* Arka Plan Rengi */}
                    <div className="space-y-2">
                      <Label htmlFor="backgroundColor" className="text-xs md:text-sm">
                        {t.backgroundColor}
                      </Label>
                      <div className="flex gap-2">
                        <Input
                          id="backgroundColor"
                          type="color"
                          value={widgetSettings.backgroundColor}
                          onChange={(e) =>
                            handleSettingChange(
                              "backgroundColor",
                              e.target.value
                            )
                          }
                          className="w-16 md:w-20 h-9 md:h-10"
                        />
                        <Input
                          value={widgetSettings.backgroundColor}
                          onChange={(e) =>
                            handleSettingChange(
                              "backgroundColor",
                              e.target.value
                            )
                          }
                          placeholder="#ffffff"
                          className="flex-1 text-xs md:text-sm"
                        />
                      </div>
                    </div>

                    {/* Buton Stili */}
                    <div className="space-y-2">
                      <Label htmlFor="buttonStyle" className="text-xs md:text-sm">{t.buttonStyle}</Label>
                      <Select
                        value={widgetSettings.buttonStyle}
                        onValueChange={(value) =>
                          handleSettingChange("buttonStyle", value)
                        }
                      >
                        <SelectTrigger id="buttonStyle" className="text-xs md:text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="rounded-full" className="text-xs md:text-sm">
                            {t.rounded}
                          </SelectItem>
                          <SelectItem value="rounded-lg" className="text-xs md:text-sm">
                            {t.roundedLarge}
                          </SelectItem>
                          <SelectItem value="rounded" className="text-xs md:text-sm">
                            {t.roundedSmall}
                          </SelectItem>
                          <SelectItem value="rounded-none" className="text-xs md:text-sm">
                            {t.roundedNone}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Yazı Tipi */}
                    <div className="space-y-2">
                      <Label htmlFor="fontFamily" className="text-xs md:text-sm">{t.fontFamily}</Label>
                      <Select
                        value={widgetSettings.fontFamily}
                        onValueChange={(value) =>
                          handleSettingChange("fontFamily", value)
                        }
                      >
                        <SelectTrigger id="fontFamily" className="text-xs md:text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Inter, sans-serif" className="text-xs md:text-sm">
                            Inter
                          </SelectItem>
                          <SelectItem value="Roboto, sans-serif" className="text-xs md:text-sm">
                            Roboto
                          </SelectItem>
                          <SelectItem value="Open Sans, sans-serif" className="text-xs md:text-sm">
                            Open Sans
                          </SelectItem>
                          <SelectItem value="Poppins, sans-serif">
                            Poppins
                          </SelectItem>
                          <SelectItem value="Montserrat, sans-serif">
                            Montserrat
                          </SelectItem>
                          <SelectItem value="Arial, sans-serif">
                            Arial
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Font Boyutu */}
                    <div className="space-y-2">
                      <Label htmlFor="fontSize">{t.fontSize}</Label>
                      <Select
                        value={widgetSettings.fontSize}
                        onValueChange={(value) =>
                          handleSettingChange("fontSize", value)
                        }
                      >
                        <SelectTrigger id="fontSize">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="12px">{t.small} (12px)</SelectItem>
                          <SelectItem value="14px">
                            {t.medium} (14px)
                          </SelectItem>
                          <SelectItem value="16px">{t.large} (16px)</SelectItem>
                          <SelectItem value="18px">
                            {t.veryLarge} (18px)
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Tema (Dark/Light Mode) */}
                    <div className="space-y-2">
                      <Label htmlFor="theme">{t.theme}</Label>
                      <Select
                        value={widgetSettings.theme}
                        onValueChange={(value) =>
                          handleSettingChange("theme", value)
                        }
                      >
                        <SelectTrigger id="theme" className="flex items-center">
                          <div className="flex items-center gap-2">
                            {widgetSettings.theme === "dark" ? (
                              <Moon className="w-4 h-4" />
                            ) : (
                              <Sun className="w-4 h-4" />
                            )}
                            <SelectValue placeholder={t.theme}>
                              {widgetSettings.theme === "dark"
                                ? t.darkMode
                                : t.lightMode}
                            </SelectValue>
                          </div>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="light">
                            <div className="flex items-center">
                              <Sun className="w-4 h-4 mr-2" />
                              {t.lightMode}
                            </div>
                          </SelectItem>
                          <SelectItem value="dark">
                            <div className="flex items-center">
                              <Moon className="w-4 h-4 mr-2" />
                              {t.darkMode}
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* P2 FİKS: Button Konumu (Opsiyonel Container) */}
                    <div className="space-y-2">
                      <Label htmlFor="containerSelector">
                        {t.buttonPosition}
                      </Label>
                      <Input
                        id="containerSelector"
                        type="text"
                        value={widgetSettings.containerSelector}
                        onChange={(e) =>
                          handleSettingChange(
                            "containerSelector",
                            e.target.value
                          )
                        }
                        placeholder={t.buttonPositionPlaceholder}
                        className="flex-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {t.buttonPositionDescription}
                      </p>
                    </div>

                    {/* Kayıt Et Butonu */}
                    <div className="pt-4 border-t">
                      <Button
                        className="w-full"
                        size="lg"
                        onClick={() => {
                          // Ayarları kaydet
                          setSavedWidgetSettings({ ...widgetSettings });
                          setShowSaveModal(true);
                        }}
                        style={{
                          backgroundColor: widgetSettings.primaryColor,
                          color: "#ffffff",
                        }}
                      >
                        {t.save}
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Canlı Önizleme */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Eye className="w-5 h-5 mr-2" />
                      {t.livePreview}
                    </CardTitle>
                    <CardDescription>
                      {t.livePreviewDescription}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex justify-center">
                    <div
                      className="rounded-lg p-8 relative border-2 border-dashed border-gray-300 dark:border-gray-600 overflow-hidden"
                      style={{
                        backgroundColor: isDark ? "#1f2937" : "#ffffff",
                        height: "auto",
                        minHeight: "430px",
                        width: "384px", // Modal içeriği ile aynı genişlik (max-w-sm)
                        display: "flex",
                        alignItems: "flex-end",
                        justifyContent: "center",
                        position: "relative",
                      }}
                    >
                      {/* Modal - Preview içinde, butonun üstünde */}
                      {showFeedbackModal && (
                        <div
                          className="absolute inset-0 z-50 flex items-center justify-center bg-black/20"
                          onClick={() => setShowFeedbackModal(false)}
                        >
                          <div
                            className="rounded-lg shadow-xl w-full max-w-sm p-6 animate-in fade-in-0 zoom-in-95"
                            style={{
                              backgroundColor:
                                widgetSettings.theme === "dark"
                                  ? "#1f2937"
                                  : widgetSettings.backgroundColor,
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {!showFeedbackForm ? (
                              <>
                                <div className="flex items-center justify-between mb-4">
                                  <h3
                                    className={`text-lg font-semibold ${widgetSettings.theme === "dark"
                                      ? "text-white"
                                      : "text-gray-900"
                                      }`}
                                  >
                                    {t.feedback}
                                  </h3>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 w-6 p-0 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
                                    onClick={() => setShowFeedbackModal(false)}
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </div>
                                <div className="space-y-3">
                                  {/* Ana Geri Bildirim Seçenekleri */}
                                  <div className="space-y-2">
                                    <Button
                                      className={`w-full justify-start h-auto py-4 px-4 rounded-lg border ${widgetSettings.theme === "dark"
                                        ? "bg-gray-700 hover:bg-gray-600 text-white border-gray-600"
                                        : "bg-white hover:bg-gray-50 text-gray-900 border-gray-200"
                                        }`}
                                      onClick={() => {
                                        setSelectedFeedbackType("feature");
                                        setFeedbackForm({
                                          ...feedbackForm,
                                          type: "feature",
                                        });
                                        setShowFeedbackForm(true);
                                      }}
                                    >
                                      <span className="font-medium">
                                        {t.featureRequest}
                                      </span>
                                    </Button>

                                    <Button
                                      className={`w-full justify-start h-auto py-4 px-4 rounded-lg border ${widgetSettings.theme === "dark"
                                        ? "bg-gray-700 hover:bg-gray-600 text-white border-gray-600"
                                        : "bg-white hover:bg-gray-50 text-gray-900 border-gray-200"
                                        }`}
                                      onClick={() => {
                                        setSelectedFeedbackType("bug");
                                        setFeedbackForm({
                                          ...feedbackForm,
                                          type: "bug",
                                        });
                                        setShowFeedbackForm(true);
                                      }}
                                    >
                                      <span className="font-medium">
                                        {t.bug}
                                      </span>
                                    </Button>

                                    <Button
                                      className={`w-full justify-start h-auto py-4 px-4 rounded-lg border ${widgetSettings.theme === "dark"
                                        ? "bg-gray-700 hover:bg-gray-600 text-white border-gray-600"
                                        : "bg-white hover:bg-gray-50 text-gray-900 border-gray-200"
                                        }`}
                                      onClick={() => {
                                        setSelectedFeedbackType("other");
                                        setFeedbackForm({
                                          ...feedbackForm,
                                          type: "other",
                                        });
                                        setShowFeedbackForm(true);
                                      }}
                                    >
                                      <span className="font-medium">
                                        {t.other}
                                      </span>
                                    </Button>
                                  </div>

                                  {/* Ek Bilgi ve Navigasyon Seçenekleri */}
                                  <div
                                    className={`pt-2 border-t space-y-2 ${widgetSettings.theme === "dark"
                                      ? "border-gray-700"
                                      : "border-gray-200"
                                      }`}
                                  >
                                    <Button
                                      variant="ghost"
                                      className={`w-full justify-start h-auto py-3 px-4 rounded-lg ${widgetSettings.theme === "dark"
                                        ? "text-gray-300 hover:bg-gray-700 hover:text-white"
                                        : "text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                                        }`}
                                      onClick={() => {
                                        const tenantSlug = activeProject?.slug;
                                        if (tenantSlug) {
                                          window.open(
                                            `/feedback/${tenantSlug}`,
                                            "_blank"
                                          );
                                        } else {
                                          toast.error(t.activeProjectNotFound);
                                        }
                                        setShowFeedbackModal(false);
                                      }}
                                    >
                                      <span className="text-sm">💬</span>
                                      <span className="text-sm ml-2">
                                        {t.viewFeedbacks || "View Feedbacks & Comments"}
                                      </span>
                                    </Button>

                                    <Button
                                      variant="ghost"
                                      className={`w-full justify-start h-auto py-3 px-4 rounded-lg ${widgetSettings.theme === "dark"
                                        ? "text-gray-300 hover:bg-gray-700 hover:text-white"
                                        : "text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                                        }`}
                                      onClick={() => {
                                        const tenantSlug = activeProject?.slug;
                                        if (tenantSlug) {
                                          window.open(
                                            `/roadmap/${tenantSlug}`,
                                            "_blank"
                                          );
                                        } else {
                                          toast.error(t.activeProjectNotFound);
                                        }
                                        setShowFeedbackModal(false);
                                      }}
                                    >
                                      <BookOpen className="w-4 h-4 mr-3 text-purple-500" />
                                      <span className="text-sm">
                                        {t.roadmap}
                                      </span>
                                    </Button>

                                    <Button
                                      variant="ghost"
                                      className={`w-full justify-start h-auto py-3 px-4 rounded-lg ${widgetSettings.theme === "dark"
                                        ? "text-gray-300 hover:bg-gray-700 hover:text-white"
                                        : "text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                                        }`}
                                      onClick={() => {
                                        const tenantSlug = activeProject?.slug;
                                        if (tenantSlug) {
                                          window.open(
                                            `/changelog/${tenantSlug}`,
                                            "_blank"
                                          );
                                        } else {
                                          toast.error(t.activeProjectNotFound);
                                        }
                                        setShowFeedbackModal(false);
                                      }}
                                    >
                                      <Bell className="w-4 h-4 mr-3 text-cyan-500" />
                                      <span className="text-sm">
                                        {t.changelog}
                                      </span>
                                    </Button>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <>
                                {/* Feedback Form */}
                                <div className="flex items-center mb-4">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 w-6 p-0 mr-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
                                    onClick={() => {
                                      setShowFeedbackForm(false);
                                      setFeedbackForm({
                                        title: "",
                                        description: "",
                                        type: "feature",
                                        submitter_name: "",
                                        submitter_email: "",
                                      });
                                    }}
                                  >
                                    <ArrowLeft className="h-4 w-4" />
                                  </Button>
                                  <h3
                                    className={`text-lg font-semibold flex-1 ${widgetSettings.theme === "dark"
                                      ? "text-white"
                                      : "text-gray-900"
                                      }`}
                                  >
                                    {selectedFeedbackType === "feature" &&
                                      `${t.featureRequest}`}
                                    {selectedFeedbackType === "bug" &&
                                      `${t.bug} ${language === "tr"
                                        ? "Raporu"
                                        : language === "de"
                                          ? "Bericht"
                                          : language === "es"
                                            ? "Reporte"
                                            : language === "fr"
                                              ? "Rapport"
                                              : "Report"
                                      }`}
                                    {selectedFeedbackType === "other" &&
                                      `${t.other}`}
                                  </h3>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 w-6 p-0 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
                                    onClick={() => {
                                      setShowFeedbackModal(false);
                                      setShowFeedbackForm(false);
                                      setFeedbackForm({
                                        title: "",
                                        description: "",
                                        type: "feature",
                                        submitter_name: "",
                                        submitter_email: "",
                                      });
                                    }}
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </div>
                                <form
                                  onSubmit={(e) => {
                                    e.preventDefault();
                                    toast.success(t.feedbackSubmitted);
                                    setShowFeedbackModal(false);
                                    setShowFeedbackForm(false);
                                    setFeedbackForm({
                                      title: "",
                                      description: "",
                                      type: "feature",
                                      submitter_name: "",
                                      submitter_email: "",
                                    });
                                  }}
                                  className="space-y-4"
                                >
                                  <div>
                                    <Label
                                      className={`text-sm font-medium ${widgetSettings.theme === "dark"
                                        ? "text-gray-300"
                                        : "text-gray-700"
                                        }`}
                                    >
                                      {t.title} *
                                    </Label>
                                    <Input
                                      value={feedbackForm.title}
                                      onChange={(e) =>
                                        setFeedbackForm({
                                          ...feedbackForm,
                                          title: e.target.value,
                                        })
                                      }
                                      placeholder={t.titlePlaceholder}
                                      required
                                      className={
                                        widgetSettings.theme === "dark"
                                          ? "bg-gray-700 text-white border-gray-600"
                                          : ""
                                      }
                                    />
                                  </div>
                                  <div>
                                    <Label
                                      className={`text-sm font-medium ${widgetSettings.theme === "dark"
                                        ? "text-gray-300"
                                        : "text-gray-700"
                                        }`}
                                    >
                                      {t.description} *
                                    </Label>
                                    <Textarea
                                      value={feedbackForm.description}
                                      onChange={(e) =>
                                        setFeedbackForm({
                                          ...feedbackForm,
                                          description: e.target.value,
                                        })
                                      }
                                      placeholder={t.descriptionPlaceholder}
                                      required
                                      rows={3}
                                      className={
                                        widgetSettings.theme === "dark"
                                          ? "bg-gray-700 text-white border-gray-600"
                                          : ""
                                      }
                                    />
                                  </div>
                                  <div className="grid grid-cols-2 gap-4">
                                    <div>
                                      <Label
                                        className={`text-sm font-medium ${widgetSettings.theme === "dark"
                                          ? "text-gray-300"
                                          : "text-gray-700"
                                          }`}
                                      >
                                        {t.yourName}
                                      </Label>
                                      <Input
                                        value={feedbackForm.submitter_name}
                                        onChange={(e) =>
                                          setFeedbackForm({
                                            ...feedbackForm,
                                            submitter_name: e.target.value,
                                          })
                                        }
                                        placeholder={t.yourNamePlaceholder}
                                        className={
                                          widgetSettings.theme === "dark"
                                            ? "bg-gray-700 text-white border-gray-600"
                                            : ""
                                        }
                                      />
                                    </div>
                                    <div>
                                      <Label
                                        className={`text-sm font-medium ${widgetSettings.theme === "dark"
                                          ? "text-gray-300"
                                          : "text-gray-700"
                                          }`}
                                      >
                                        {t.yourEmail} *
                                      </Label>
                                      <Input
                                        type="email"
                                        value={feedbackForm.submitter_email}
                                        onChange={(e) =>
                                          setFeedbackForm({
                                            ...feedbackForm,
                                            submitter_email: e.target.value,
                                          })
                                        }
                                        placeholder={t.yourEmailPlaceholder}
                                        required
                                        className={
                                          widgetSettings.theme === "dark"
                                            ? "bg-gray-700 text-white border-gray-600"
                                            : ""
                                        }
                                      />
                                    </div>
                                  </div>
                                  <Button
                                    type="submit"
                                    className="w-full"
                                    style={{
                                      backgroundColor:
                                        widgetSettings.primaryColor,
                                      color: "#ffffff",
                                    }}
                                  >
                                    {t.submitFeedback}
                                  </Button>
                                </form>
                              </>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Buton - Her zaman orta altta sabit */}
                      <div className="absolute z-40" style={buttonPosition}>
                        <button
                          className={`${widgetSettings.buttonStyle} shadow-lg flex items-center justify-center px-6 py-3 font-medium transition-colors`}
                          style={{
                            backgroundColor: widgetSettings.primaryColor,
                            color: "#ffffff",
                            fontFamily: widgetSettings.fontFamily,
                            fontSize: widgetSettings.fontSize,
                            border: "none",
                            borderRadius:
                              widgetSettings.buttonStyle === "rounded-full"
                                ? "9999px"
                                : widgetSettings.buttonStyle === "rounded-lg"
                                  ? "0.5rem"
                                  : widgetSettings.buttonStyle === "rounded"
                                    ? "0.25rem"
                                    : "0",
                            cursor: "pointer",
                            minHeight:
                              widgetSettings.buttonSize === "lg"
                                ? "48px"
                                : widgetSettings.buttonSize === "md"
                                  ? "40px"
                                  : "32px",
                            padding:
                              widgetSettings.buttonSize === "lg"
                                ? "12px 24px"
                                : widgetSettings.buttonSize === "md"
                                  ? "10px 20px"
                                  : "8px 16px",
                          }}
                          onClick={() => setShowFeedbackModal(true)}
                          data-testid="widget-preview-button"
                        >
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            style={{ marginRight: '8px' }}
                          >
                            <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path>
                          </svg>
                          <span
                            style={{
                              fontFamily: widgetSettings.fontFamily,
                              fontSize: widgetSettings.fontSize,
                            }}
                          >
                            {t.featureRequest}
                          </span>
                        </button>
                      </div>
                      {!showFeedbackModal && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <p className="text-sm text-gray-500">
                            {t.websiteContent}
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="code" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Code className="w-5 h-5 mr-2" />
                    {t.integrationCode}
                  </CardTitle>
                  <CardDescription>{t.selectFramework}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Framework Seçimi */}
                  <div>
                    <Label className="text-sm font-medium mb-3 block">
                      {t.selectFramework}
                    </Label>
                    <div className="flex gap-3 w-full">
                      <button
                        onClick={() => setSelectedFramework("react")}
                        className={`p-4 border-2 rounded-lg transition-all hover:scale-105 flex-1 ${selectedFramework === "react"
                          ? "border-cyan-600 bg-cyan-50 dark:bg-cyan-900/20 dark:border-cyan-500"
                          : "border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600"
                          }`}
                      >
                        <img
                          src={isDark ? "/entegrasyonIconLight/react_icon_light-64.webp" : "/entegrasyonIcon/react_icon-64.webp"}
                          alt="React"
                          className="w-8 h-8 mb-2 mx-auto"
                        />
                        <div className="text-xs font-medium text-gray-900 dark:text-gray-100">React</div>
                      </button>
                      <button
                        onClick={() => setSelectedFramework("vue")}
                        className={`p-4 border-2 rounded-lg transition-all hover:scale-105 flex-1 ${selectedFramework === "vue"
                          ? "border-cyan-600 bg-cyan-50 dark:bg-cyan-900/20 dark:border-cyan-500"
                          : "border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600"
                          }`}
                      >
                        <img
                          src={isDark ? "/entegrasyonIconLight/vue_icon_light-64.webp" : "/entegrasyonIcon/vue_icon-64.webp"}
                          alt="Vue"
                          className="w-8 h-8 mb-2 mx-auto"
                        />
                        <div className="text-xs font-medium text-gray-900 dark:text-gray-100">Vue</div>
                      </button>
                      <button
                        onClick={() => setSelectedFramework("nuxt")}
                        className={`p-4 border-2 rounded-lg transition-all hover:scale-105 flex-1 ${selectedFramework === "nuxt"
                          ? "border-cyan-600 bg-cyan-50 dark:bg-cyan-900/20 dark:border-cyan-500"
                          : "border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600"
                          }`}
                      >
                        <img
                          src={isDark ? "/entegrasyonIconLight/nuxt_icon_light-64.webp" : "/entegrasyonIcon/nuxt_icon-64.webp"}
                          alt="Nuxt"
                          className="w-8 h-8 mb-2 mx-auto"
                        />
                        <div className="text-xs font-medium text-gray-900 dark:text-gray-100">Nuxt</div>
                      </button>
                      <button
                        onClick={() => setSelectedFramework("next")}
                        className={`p-4 border-2 rounded-lg transition-all hover:scale-105 flex-1 ${selectedFramework === "next"
                          ? "border-cyan-600 bg-cyan-50 dark:bg-cyan-900/20 dark:border-cyan-500"
                          : "border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600"
                          }`}
                      >
                        <img
                          src={isDark ? "/entegrasyonIconLight/nextjs_icon_light-64.webp" : "/entegrasyonIcon/nextjs_icon-64.webp"}
                          alt="Next.js"
                          className="w-8 h-8 mb-2 mx-auto"
                        />
                        <div className="text-xs font-medium text-gray-900 dark:text-gray-100">Next.js</div>
                      </button>
                      <button
                        onClick={() => setSelectedFramework("wordpress")}
                        className={`p-4 border-2 rounded-lg transition-all hover:scale-105 flex-1 ${selectedFramework === "wordpress"
                          ? "border-cyan-600 bg-cyan-50 dark:bg-cyan-900/20 dark:border-cyan-500"
                          : "border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600"
                          }`}
                      >
                        <img
                          src={isDark ? "/entegrasyonIconLight/wordpress_icon_light-64.webp" : "/entegrasyonIcon/wordpress_icon-64.webp"}
                          alt="WordPress"
                          className="w-8 h-8 mb-2 mx-auto"
                        />
                        <div className="text-xs font-medium text-gray-900 dark:text-gray-100">WordPress</div>
                      </button>
                      <button
                        onClick={() => setSelectedFramework("other")}
                        className={`p-4 border-2 rounded-lg transition-all hover:scale-105 flex-1 ${selectedFramework === "other"
                          ? "border-cyan-600 bg-cyan-50 dark:bg-cyan-900/20 dark:border-cyan-500"
                          : "border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600"
                          }`}
                      >
                        <img
                          src={isDark ? "/entegrasyonIconLight/html_icon_light-64.webp" : "/entegrasyonIcon/html_icon-64.webp"}
                          alt="Other"
                          className="w-8 h-8 mb-2 mx-auto"
                        />
                        <div className="text-xs font-medium text-gray-900 dark:text-gray-100">Other</div>
                      </button>
                    </div>
                  </div>

                  {/* Kod Gösterimi */}
                  <div>
                    <Label className="text-sm font-medium mb-2 block">
                      {selectedFramework === "react" && t.reactCode}
                      {selectedFramework === "vue" && t.vueCode}
                      {selectedFramework === "nuxt" && t.nuxtCode}
                      {selectedFramework === "next" && t.nextCode}
                      {selectedFramework === "wordpress" && t.wordpressCode}
                      {selectedFramework === "other" && t.generalGuide}
                    </Label>
                    {selectedFramework === "other" ? (
                      <div className="relative">
                        <pre
                          className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm max-h-[600px] overflow-y-auto whitespace-pre-line"
                          data-testid="widget-code"
                        >
                          <code>{widgetCode}</code>
                        </pre>
                        <Button
                          size="sm"
                          variant="secondary"
                          className="absolute top-2 right-2"
                          onClick={handleCopy}
                          data-testid="copy-widget-code-button"
                        >
                          {copied ? (
                            <>{t.copied}</>
                          ) : (
                            <>
                              <Copy className="w-4 h-4 mr-2" />
                              {t.copyContent}
                            </>
                          )}
                        </Button>
                      </div>
                    ) : (
                      <div className="relative">
                        <pre
                          className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm"
                          data-testid="widget-code"
                        >
                          <code>{widgetCode}</code>
                        </pre>
                        <Button
                          size="sm"
                          variant="secondary"
                          className="absolute top-2 right-2"
                          onClick={handleCopy}
                          data-testid="copy-widget-code-button"
                        >
                          {copied ? (
                            <>{t.copied}</>
                          ) : (
                            <>
                              <Copy className="w-4 h-4 mr-2" />
                              {t.copyCode}
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {selectedFramework !== "other" && (
                <Card>
                  <CardHeader>
                    <CardTitle>{t.installationInstructions}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600 dark:text-gray-400">
                      {selectedFramework === "react" && (
                        <>
                          <li>{t.reactInstall1}</li>
                          <li>{t.reactInstall2}</li>
                          <li>
                            {t.reactInstall3}{" "}
                            <code className="bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-1 rounded">
                              &lt;FeedbackWidget /&gt;
                            </code>
                          </li>
                          <li>{t.reactInstall4}</li>
                          <li>{t.reactInstall5}</li>
                        </>
                      )}
                      {selectedFramework === "vue" && (
                        <>
                          <li>{t.vueInstall1}</li>
                          <li>{t.vueInstall2}</li>
                          <li>{t.vueInstall3}</li>
                          <li>{t.vueInstall4}</li>
                          <li>{t.vueInstall5}</li>
                        </>
                      )}
                      {selectedFramework === "nuxt" && (
                        <>
                          <li>{t.nuxtInstall1}</li>
                          <li>
                            {t.nuxtInstall2}{" "}
                            <code className="bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-1 rounded">
                              plugins/feedback-widget.client.js
                            </code>{" "}
                            {t.nuxtInstall3}
                          </li>
                          <li>{t.nuxtInstall4}</li>
                          <li>{t.nuxtInstall5}</li>
                          <li>{t.nuxtInstall6}</li>
                        </>
                      )}
                      {selectedFramework === "next" && (
                        <>
                          <li>{t.nextInstall1}</li>
                          <li>
                            {t.nextInstall2}{" "}
                            <code className="bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-1 rounded">
                              components/FeedbackWidget.tsx
                            </code>{" "}
                            {t.nextInstall3}
                          </li>
                          <li>
                            {t.nextInstall4}{" "}
                            <code className="bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-1 rounded">
                              &lt;FeedbackWidget /&gt;
                            </code>
                          </li>
                          <li>{t.nextInstall5}</li>
                          <li>{t.nextInstall6}</li>
                        </>
                      )}
                      {selectedFramework === "wordpress" && (
                        <>
                          <li>{t.wordpressInstall1}</li>
                          <li>{t.wordpressInstall2}</li>
                          <li>{t.wordpressInstall3}</li>
                          <li>{t.wordpressInstall4}</li>
                          <li>{t.wordpressInstall5}</li>
                          <li>{t.wordpressInstall6}</li>
                        </>
                      )}
                    </ol>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>

          {/* Kayıt Başarılı Modal */}
          <Dialog open={showSaveModal} onOpenChange={setShowSaveModal}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4">
                    <svg
                      className="w-6 h-6 text-green-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <span>{t.changesSaved}</span>
                </DialogTitle>
                <DialogDescription className="pt-4">
                  {t.changesSavedDescription}
                </DialogDescription>
              </DialogHeader>
              <div className="flex gap-3 mt-4">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowSaveModal(false)}
                >
                  {t.close}
                </Button>
                <Button
                  className="flex-1"
                  onClick={() => {
                    setShowSaveModal(false);
                    handleTabChange("code");
                  }}
                  style={{
                    backgroundColor: widgetSettings.primaryColor,
                    color: "#ffffff",
                  }}
                >
                  {t.getIntegrationCode}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Unsaved Changes Warning Dialog */}
          <AlertDialog
            open={showUnsavedChangesDialog}
            onOpenChange={setShowUnsavedChangesDialog}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {t.unsavedChangesTitle || "Kaydedilmemiş Değişiklikler"}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {t.unsavedChangesMessage ||
                    "Özelleştirme ayarlarınızda kaydedilmemiş değişiklikler var. Entegrasyon koduna geçerseniz bu değişiklikler kaybolacak ve kod mevcut ayarlardan farklı olacaktır."}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel onClick={handleCancelTabChange}>
                  {t.cancel || "İptal"}
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleConfirmTabChange}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {t.continueAnyway || "Yine de Devam Et"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </>
  );
};
